import { useMediaQuery, useTheme } from "@mui/material";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import { IApiEntitySmpAgTemplate } from "@smp/api";
import {
  useVirtualizer,
  VirtualItem,
  VirtualizerOptions,
} from "@tanstack/react-virtual";
import { useCallback, useMemo, useRef, useState } from "react";

import { IVirtualItem } from "@/components/features/polotno_templates";
import { UnsplashPayload } from "@/queries/polotno";

const RESIZE_END_DELAY = 10;
const MIN_COLUMN_WIDTH = 126;
const ESTIMATE_SIZE = 274;
const GAP = 24;
const GAP_MOBILE = 10;
const CARD_PADDING = 4;
const SSR_PARENT_WIDTH = 1000;

type ItemBase<T> = {
  key: string;
  estimateSize?: number;
} & T;

type Item =
  | ItemBase<{
      type: "create-btn";
    }>
  | ItemBase<{
      type: "loader";
    }>
  | ItemBase<{
      type: "template";
      payload: IVirtualItem<IApiEntitySmpAgTemplate>;
    }>
  | ItemBase<{
      type: "unsplash";
      payload: UnsplashPayload["results"][number];
    }>;

export const useTemplateVirtualizer = (
  items: Item[],
  parentRef: HTMLDivElement | null,
  options?: Partial<VirtualizerOptions<HTMLDivElement, HTMLDivElement>> & {
    disableScrollOnLaneChange?: boolean;
  },
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [parentWidth, setParentWidth] = useState(
    import.meta.env.SSR ? SSR_PARENT_WIDTH : parentRef?.clientWidth || 0,
  );
  const restoreScrollData = useRef<{
    virtualItemIndex?: number;
  } | null>(null);
  const restoreScrollTimeoutId = useRef(0);
  const resizeCounter = useRef(0);

  const lanes = useMemo(() => {
    if (!parentWidth) {
      return options?.lanes || 0;
    }

    const maxColumnNum = parentWidth > 1280 ? 8 : 6;

    return Math.min(
      Math.floor(
        parentWidth / (MIN_COLUMN_WIDTH + (isMobile ? GAP_MOBILE : GAP)),
      ),
      maxColumnNum,
    );
  }, [isMobile, options?.lanes, parentWidth]);

  const estimateSizeMap = useMemo<Map<number, number> | undefined>(() => {
    if (!parentWidth) {
      return undefined;
    }

    const totalGapWidth = (isMobile ? GAP_MOBILE : GAP) * (lanes - 1);
    const columnWidth = (parentWidth - totalGapWidth) / lanes + CARD_PADDING;

    return items.reduce((acc, item, idx) => {
      if (item.type === "create-btn" || item.type === "loader") {
        acc.set(idx, columnWidth);
      }

      if (item.type === "template") {
        const width = item.payload.meta?.width ?? ESTIMATE_SIZE;
        const height = item.payload.meta?.height ?? ESTIMATE_SIZE;
        const ratio = height / width;

        acc.set(idx, columnWidth * ratio);
      }

      if (item.type === "unsplash") {
        const width = item.payload.width || ESTIMATE_SIZE;
        const height = item.payload.height || ESTIMATE_SIZE;
        const ratio = height / width;

        acc.set(idx, columnWidth * ratio);
      }

      return acc;
    }, new Map());
  }, [isMobile, items, lanes, parentWidth]);

  const getEstimateSize = useCallback(
    (index: number) => estimateSizeMap?.get(index) || ESTIMATE_SIZE,
    [estimateSizeMap],
  );

  const getItemKey = useCallback(
    (index: number) => items[index].key || "0",
    [items],
  );

  const getScrollElement = useCallback(() => parentRef, [parentRef]);

  const virtualizer = useVirtualizer({
    count: items.length,
    estimateSize: getEstimateSize,
    getItemKey: getItemKey,
    getScrollElement: getScrollElement,
    gap: (isMobile ? GAP_MOBILE : GAP) - CARD_PADDING,
    overscan: lanes * 2,
    measureElement: (element) => element.clientHeight + CARD_PADDING * 2,
    onChange: (instance) => {
      if (restoreScrollData.current?.virtualItemIndex === undefined) {
        return;
      }

      window.clearTimeout(restoreScrollTimeoutId.current);

      restoreScrollTimeoutId.current = window.setTimeout(() => {
        if (restoreScrollData.current?.virtualItemIndex !== undefined) {
          const [itemOffset] = instance.getOffsetForIndex(
            restoreScrollData.current.virtualItemIndex,
            "start",
          ) || [0];

          instance.scrollToOffset(itemOffset);
        }

        restoreScrollData.current = null;
      }, 100);
    },
    ...options,
    enabled: options?.enabled !== false && lanes > 0,
    lanes,
    initialRect: import.meta.env.SSR
      ? {
          width: SSR_PARENT_WIDTH,
          height: Infinity,
        }
      : undefined,
  });

  const calculateStyles = useCallback(
    ({ lane, size, start, index }: VirtualItem) => {
      const widthPrc = 100 / virtualizer.options.lanes;
      const gapPx =
        (virtualizer.options.gap * (virtualizer.options.lanes - 1)) /
        virtualizer.options.lanes;
      const leftOffsetPx = (virtualizer.options.gap - gapPx) * lane;

      if (import.meta.env.SSR) {
        return undefined;
      }

      return {
        height: `${size || estimateSizeMap?.get(index) || ESTIMATE_SIZE}px`,
        padding: `${CARD_PADDING}px`,
        position: "absolute",
        top: 0,
        left: `calc(${lane * widthPrc}% + ${leftOffsetPx}px)`,
        width: `calc(${widthPrc}% - ${gapPx}px)`,
        transform: `translateY(${start}px)`,
      } as const;
    },
    [estimateSizeMap, virtualizer.options.gap, virtualizer.options.lanes],
  );

  const getTotalSizeCustom = useCallback(() => {
    const measurements = virtualizer.measurementsCache.slice(
      (virtualizer.options.lanes || 1) * -1,
    );

    const maxEnd = measurements.reduce((acc, item) => {
      acc = Math.max(acc, item.end);

      return acc;
    }, 0);

    return (
      (maxEnd || virtualizer.options.paddingStart) -
      virtualizer.options.paddingEnd +
      (virtualizer.options.count ? virtualizer.options.gap : 0)
    );
  }, [
    virtualizer.measurementsCache,
    virtualizer.options.count,
    virtualizer.options.gap,
    virtualizer.options.lanes,
    virtualizer.options.paddingEnd,
    virtualizer.options.paddingStart,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (parentRef) {
      const resizeObserver = new ResizeObserver(() => {
        if (
          parentWidth !== parentRef.clientWidth &&
          resizeCounter.current === 0
        ) {
          if (options?.disableScrollOnLaneChange) {
            return;
          }

          const scrollOffset = virtualizer.scrollOffset || 0;
          const firstVisibleItem = virtualizer.measurementsCache?.find(
            (item) => item.start >= scrollOffset,
          );

          if (firstVisibleItem && firstVisibleItem.index >= lanes) {
            restoreScrollData.current = {
              virtualItemIndex: firstVisibleItem?.index,
            };
          }
        }

        setParentWidth(parentRef.clientWidth);

        resizeCounter.current++;

        setTimeout(() => (resizeCounter.current = 0), RESIZE_END_DELAY);
      });

      resizeObserver.observe(parentRef);

      return () => resizeObserver.disconnect();
    }
  }, [
    setParentWidth,
    parentRef,
    options?.disableScrollOnLaneChange,
    virtualizer.scrollOffset,
    virtualizer.measurementsCache,
    lanes,
    parentWidth,
  ]);

  useIsomorphicLayoutEffect(() => {
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = () => false;
  }, [virtualizer]);

  useIsomorphicLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer.options.lanes]);

  return {
    calculateStyles,
    getTotalSizeCustom,
    virtualizer,
  };
};
